import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { purchaseApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

export function SuppliersPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all'|'active'|'inactive'>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['suppliers', q],
    queryFn: () => purchaseApi.listSuppliers({ q: q || undefined }),
  });

  const createMut = useMutation({
    mutationFn: (p: any) => purchaseApi.createSupplier(p),
    onSuccess: () => { toast.success('Supplier created'); qc.invalidateQueries({ queryKey: ['suppliers'] }); setShowCreate(false); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, payload }: any) => purchaseApi.updateSupplier(id, payload),
    onSuccess: () => { toast.success('Supplier updated'); qc.invalidateQueries({ queryKey: ['suppliers'] }); setEditing(null); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, status }: any) => purchaseApi.updateSupplier(id, { status }),
    onSuccess: (_d, v) => { toast.success(v.status === 'active' ? 'Supplier activated' : 'Supplier deactivated'); qc.invalidateQueries({ queryKey: ['suppliers'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => purchaseApi.deleteSupplier(id),
    onSuccess: (r: any) => { toast.success(r?.message || 'Supplier deleted'); qc.invalidateQueries({ queryKey: ['suppliers'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = (data ?? []) as any[];
    const withRating = rows.filter((s) => s.performance_rating != null && !isNaN(parseFloat(s.performance_rating)));
    const avgRating = withRating.length > 0 ? withRating.reduce((a, s) => a + parseFloat(s.performance_rating), 0) / withRating.length : null;
    const k = { total: rows.length, active: rows.filter((s) => s.status === 'active').length, inactive: rows.filter((s) => s.status !== 'active').length, avgRating };
    const filtered = rows.filter((s) => statusFilter === 'all' || s.status === statusFilter);
    return { kpis: k, filteredRows: filtered };
  }, [data, statusFilter]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader eyebrow="Purchasing" title="Suppliers"
        subtitle="Your vendor relationships — contacts, payment terms, and performance ratings."
        actions={<button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New supplier</button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total suppliers" value={kpis.total} />
        <KpiTile label="Active" value={kpis.active} accent="sage" />
        <KpiTile label="Inactive" value={kpis.inactive} />
        <KpiTile label="Avg rating" value={kpis.avgRating != null ? `${kpis.avgRating.toFixed(1)} ★` : '—'} accent={kpis.avgRating != null && kpis.avgRating >= 4 ? 'sage' : 'default'} />
      </div>

      <div className="card p-1.5 mb-4 flex gap-2 items-center flex-wrap">
        <div className="flex-1 min-w-48 flex items-center gap-2 px-3">
          <SearchIcon />
          <input type="text" placeholder="Search by code, name, email…" value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm placeholder:text-ink-400 py-2 min-w-0" />
        </div>
        <div className="flex gap-1 bg-canvas-subtle rounded-pill p-1">
          {(['all','active','inactive'] as const).map((k) => (
            <button key={k} onClick={() => setStatusFilter(k)}
              className={clsx('px-3 py-1 text-xs font-semibold rounded-pill transition-all capitalize',
                statusFilter === k ? 'bg-canvas-elevated text-ink-900 shadow-xs' : 'text-ink-600 hover:text-ink-900')}>
              {k === 'all' ? 'All' : k}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? <div className="card"><LoadingState rows={6} /></div>
        : error ? <ErrorState error={error} />
        : !filteredRows.length ? (
          <div className="card">
            <EmptyState message={q ? 'No suppliers match' : 'No suppliers yet'}
              hint={q ? 'Try a different keyword.' : 'Add your first supplier to start issuing purchase orders.'}
              action={!q && <button className="btn-primary" onClick={() => setShowCreate(true)}><PlusIcon /> New supplier</button>}
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Supplier</th><th>Contact</th><th>Payment terms</th>
                    <th>Rating</th><th>Status</th><th className="text-right w-44">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((s: any) => (
                    <tr key={s.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <SupplierAvatar name={s.legal_name} />
                          <div className="min-w-0">
                            <div className="font-semibold text-ink-900 truncate">{s.legal_name}</div>
                            <div className="font-mono text-xs text-ink-500">{s.code}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-sm text-ink-600">
                        {s.email && <div className="truncate">{s.email}</div>}
                        {s.phone && <div className="text-xs text-ink-500">{s.phone}</div>}
                        {!s.email && !s.phone && <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-sm text-ink-700">
                        <span className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 font-mono">
                          {(s.payment_terms ?? '').replace('_', ' ')}
                        </span>
                      </td>
                      <td><Rating value={s.performance_rating} /></td>
                      <td><StatusBadge status={s.status} /></td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button className="text-xs font-medium px-2 py-1 rounded hover:bg-canvas-subtle transition-colors text-ink-600"
                            onClick={() => setEditing(s)}>Edit</button>
                          <button
                            className={`text-xs font-medium px-2 py-1 rounded transition-colors ${s.status === 'active' ? 'text-ink-500 hover:bg-canvas-subtle' : 'text-sage-700 hover:bg-sage-50'}`}
                            disabled={toggleMut.isPending}
                            onClick={() => toggleMut.mutate({ id: s.id, status: s.status === 'active' ? 'inactive' : 'active' })}>
                            {s.status === 'active' ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="text-xs font-medium px-2 py-1 rounded transition-colors text-red-600 hover:bg-red-50"
                            disabled={deleteMut.isPending}
                            onClick={() => { if (window.confirm(`Delete supplier "${s.legal_name}"? If it has linked records it will be deactivated instead.`)) deleteMut.mutate(s.id); }}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <SupplierModal onSubmit={(p) => createMut.mutate(p)} onClose={() => setShowCreate(false)} loading={createMut.isPending} />}
      {editing && <SupplierModal initial={editing} onSubmit={(p) => updateMut.mutate({ id: editing.id, payload: p })} onClose={() => setEditing(null)} loading={updateMut.isPending} />}
    </div>
  );
}

function SupplierModal({ initial, onSubmit, onClose, loading }: {
  initial?: any; onSubmit: (p: any) => void; onClose: () => void; loading: boolean;
}) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    code: initial?.code ?? '',
    legal_name: initial?.legal_name ?? '',
    email: initial?.email ?? '',
    phone: initial?.phone ?? '',
    payment_terms: initial?.payment_terms ?? 'net_30',
  });
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Purchasing</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{isEdit ? 'Edit supplier' : 'New supplier'}</h2>
        {!isEdit && <p className="text-sm text-ink-500 mt-1">Set up a vendor to issue purchase orders against.</p>}
      </div>
      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Supplier code <span className="text-danger">*</span></label>
            <input className="input font-mono" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="SUP-001" disabled={isEdit} />
          </div>
          <div>
            <label className="label">Payment terms</label>
            <select className="input" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}>
              <option value="due_on_receipt">Due on receipt</option>
              <option value="net_15">Net 15</option>
              <option value="net_30">Net 30</option>
              <option value="net_60">Net 60</option>
            </select>
          </div>
        </div>
        <div>
          <label className="label">Legal name <span className="text-danger">*</span></label>
          <input className="input" value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="Acme Supplies Inc." />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Email</label>
            <input type="email" className="input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="orders@acme.com" />
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+1 555 234 5678" />
          </div>
        </div>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={loading || !form.legal_name || (!isEdit && !form.code)}
          onClick={() => onSubmit(isEdit
            ? { legal_name: form.legal_name, email: form.email || undefined, phone: form.phone || undefined, payment_terms: form.payment_terms }
            : { code: form.code, legal_name: form.legal_name, email: form.email || undefined, phone: form.phone || undefined, payment_terms: form.payment_terms })}>
          {loading ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create supplier')}
        </button>
      </div>
    </Modal>
  );
}

function SupplierAvatar({ name }: { name: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() ?? '').join('') || '?';
  const hash = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  const tones = ['bg-blue-100 text-blue-700','bg-teal-100 text-teal-700','bg-purple-100 text-purple-700','bg-amber-100 text-amber-800','bg-sage-100 text-sage-700','bg-rose-100 text-rose-700'];
  return <div className={`w-9 h-9 rounded-md flex items-center justify-center text-xs font-semibold flex-shrink-0 ${tones[hash % tones.length]}`}>{initials}</div>;
}

function Rating({ value }: { value: string|number|null|undefined }) {
  if (value == null || value === '') return <span className="text-ink-400 text-sm">—</span>;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return <span className="text-ink-400 text-sm">—</span>;
  const full = Math.floor(num);
  const hasHalf = num - full >= 0.5;
  return (
    <div className="flex items-center gap-1">
      <div className="flex text-sage-500">
        {[0,1,2,3,4].map((i) => {
          if (i < full) return <span key={i}>★</span>;
          if (i === full && hasHalf) return <span key={i} className="opacity-60">★</span>;
          return <span key={i} className="text-ink-200">★</span>;
        })}
      </div>
      <span className="text-xs text-ink-500 tabular-nums font-mono">{num.toFixed(1)}</span>
    </div>
  );
}

function KpiTile({ label, value, accent='default' }: { label: string; value: number|string; accent?: 'default'|'sage'|'amber'|'danger' }) {
  const bar = { default:'bg-ink-200', sage:'bg-sage-500', amber:'bg-amber-500', danger:'bg-danger' }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    </div>
  );
}

const ip = { width:16,height:16,viewBox:'0 0 24 24',fill:'none',stroke:'currentColor',strokeWidth:2,strokeLinecap:'round' as const,strokeLinejoin:'round' as const };
function PlusIcon() { return <svg {...ip}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon() { return <svg {...ip} className="text-ink-400 flex-shrink-0"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
